import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import SortHeader from "../../Components/SortHeader";
import { downloadExport } from "../../utils/downloadExport";

const STATUSES = ["pending", "confirmed", "preparing", "out_for_delivery", "delivered", "cancelled"];

const STATUS_STYLES = {
  pending: "bg-gray-100 text-gray-700",
  confirmed: "bg-blue-50 text-blue-700",
  preparing: "bg-amber-50 text-amber-700",
  out_for_delivery: "bg-purple-50 text-purple-700",
  delivered: "bg-green-50 text-green-700",
  cancelled: "bg-red-50 text-red-700",
};

const formatStatus = (status) => (status || "pending").replace(/_/g, " ");

const Order = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const token = localStorage.getItem("authToken");

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExport(`${API_BASE_URL}/order/export`, token, "orders-export.xlsx");
    } catch (err) {
      console.error("Error exporting orders:", err);
      alert("Failed to export orders.");
    } finally {
      setExporting(false);
    }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/order/all-orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit: 20,
          search: search || undefined,
          status: statusFilter || undefined,
          sortBy,
          sortDir,
        },
      });
      setOrders(response.data.orders);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setLoadError("Couldn't load orders. Check your connection and try again.");
      setOrders([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter, sortBy, sortDir]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(1);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/order/delete-order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(orders.filter((order) => order._id !== orderId));
    } catch (err) {
      console.error("Error deleting order:", err);
      alert("Failed to delete order.");
    }
  };

  const handleStatusChange = async (orderId, status) => {
    setUpdatingId(orderId);
    try {
      await axios.patch(
        `${API_BASE_URL}/order/${orderId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(orders.map((o) => (o._id === orderId ? { ...o, status } : o)));
    } catch (err) {
      console.error("Error updating order status:", err);
      alert("Failed to update order status.");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-ink">Orders</h1>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="text-sm font-semibold px-3 py-1.5 border border-line rounded hover:bg-paper transition disabled:opacity-50"
        >
          {exporting ? "Exporting…" : "Export to Excel"}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <input
          placeholder="Search by name, phone, or order ID…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="flex-1 min-w-[220px] p-2 border border-line rounded"
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="p-2 border border-line rounded"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {formatStatus(s)}
            </option>
          ))}
        </select>
      </div>

      {loadError && (
        <div className="flex items-center justify-between gap-3 mb-4 p-3 border border-red-200 bg-red-50 text-red-700 text-sm rounded">
          <span>{loadError}</span>
          <button onClick={fetchOrders} className="font-semibold underline shrink-0">
            Retry
          </button>
        </div>
      )}

      <div className="bg-card border border-line rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-paper text-left text-xs uppercase tracking-wide text-muted border-b border-line">
              <tr>
                <SortHeader label="Order" field="orderId" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Customer" field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th className="p-3">Items</th>
                <SortHeader label="Total" field="grandTotal" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th className="p-3">Status</th>
                <SortHeader label="Date" field="createdAt" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loading ? (
                <tr>
                  <td className="p-4 text-sub" colSpan={7}>
                    Loading…
                  </td>
                </tr>
              ) : loadError ? null : orders.length === 0 ? (
                <tr>
                  <td className="p-4 text-sub" colSpan={7}>
                    No orders match this search/filter.
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const hasOrderLines = order.orderLines && order.orderLines.length > 0;
                  const platterItemCount = (order.customPlatters || [])
                    .filter(Boolean)
                    .reduce((sum, p) => sum + (p.foodItems?.length || 0), 0);
                  const itemCount = hasOrderLines
                    ? order.orderLines.reduce((sum, l) => sum + l.quantity, 0)
                    : (order.items?.filter(Boolean).length || 0) +
                      (order.adHocItems?.reduce((sum, i) => sum + i.quantity, 0) || 0) +
                      platterItemCount;
                  const isExpanded = expandedId === order._id;

                  return (
                    <React.Fragment key={order._id}>
                      <tr
                        className="hover:bg-paper/60 cursor-pointer transition"
                        onClick={() => setExpandedId(isExpanded ? null : order._id)}
                      >
                        <td className="p-3 font-mono text-sub whitespace-nowrap">
                          #{order.orderId}
                          {order.notificationFailed && (
                            <span
                              className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary"
                              title="Staff Telegram notification failed for this order"
                            >
                              notify failed
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-ink">
                          <div className="max-w-[160px] truncate">{order.name}</div>
                          <div className="text-xs text-sub font-mono">{order.phone}</div>
                          {order.restaurant?.name && (
                            <div className="text-[10px] text-primary font-semibold uppercase tracking-wide mt-0.5">
                              {order.restaurant.name}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-sub whitespace-nowrap">{itemCount} item(s)</td>
                        <td className="p-3 text-ink font-mono whitespace-nowrap">
                          {typeof order.grandTotal === "number" ? `৳${order.grandTotal}` : "—"}
                        </td>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={order.status || "pending"}
                            onChange={(e) => handleStatusChange(order._id, e.target.value)}
                            disabled={updatingId === order._id}
                            className={`text-xs font-semibold px-2 py-1 rounded-full border-0 capitalize disabled:opacity-50 ${
                              STATUS_STYLES[order.status || "pending"]
                            }`}
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {formatStatus(s)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3 text-sub whitespace-nowrap">
                          {new Date(order.createdAt).toLocaleString("en-BD", {
                            timeZone: "Asia/Dhaka",
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="p-3 text-sub text-right">{isExpanded ? "▲" : "▼"}</td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-paper/40">
                          <td colSpan={7} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs uppercase tracking-wide text-muted font-semibold mb-1">
                                  Delivery details
                                </p>
                                <p className="text-ink">{order.address}</p>
                                <p className="text-sub">{order.phone}</p>
                                {order.email && <p className="text-sub">{order.email}</p>}
                                {order.request && (
                                  <>
                                    <p className="text-xs uppercase tracking-wide text-muted font-semibold mt-3 mb-1">
                                      Customer note
                                    </p>
                                    {/* Some older orders have unusually long
                                        text here (an auto-generated summary
                                        got concatenated into this field
                                        historically) - capped so one order
                                        can't blow out the whole row. */}
                                    <p className="text-sub max-h-24 overflow-y-auto whitespace-pre-wrap break-words border border-line rounded p-2 bg-card">
                                      {order.request}
                                    </p>
                                  </>
                                )}
                              </div>

                              <div>
                                <p className="text-xs uppercase tracking-wide text-muted font-semibold mb-1">
                                  Items
                                </p>
                                {hasOrderLines ? (
                                  <ul className="space-y-0.5">
                                    {order.orderLines.map((line, i) => (
                                      <li key={i} className="flex justify-between text-sub">
                                        <span>
                                          {line.title} × {line.quantity}
                                        </span>
                                        <span className="font-mono">৳{line.price * line.quantity}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <>
                                    <ul className="space-y-0.5">
                                      {order.items?.filter(Boolean).map((item) => (
                                        <li key={item._id} className="flex justify-between text-sub">
                                          <span>{item.title}</span>
                                          <span className="font-mono">৳{item.price}</span>
                                        </li>
                                      ))}
                                      {order.customPlatters?.filter(Boolean).map((platter) => (
                                        <li key={platter._id} className="text-sub">
                                          <span className="font-semibold">{platter.platterType}</span>
                                          <ul className="ml-3">
                                            {platter.foodItems?.map((foodItem) => (
                                              <li key={foodItem._id} className="flex justify-between">
                                                <span>{foodItem.title}</span>
                                                <span className="font-mono">৳{foodItem.price}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        </li>
                                      ))}
                                      {order.adHocItems?.map((item, i) => (
                                        <li key={i} className="flex justify-between text-sub">
                                          <span>
                                            {item.title} × {item.quantity}
                                          </span>
                                          <span className="font-mono">৳{item.price * item.quantity}</span>
                                        </li>
                                      ))}
                                    </ul>
                                    <p className="text-[11px] text-muted mt-2">
                                      Placed before per-item totals were tracked — quantities/total shown here may be incomplete.
                                    </p>
                                  </>
                                )}

                                {typeof order.grandTotal === "number" && (
                                  <div className="flex justify-between text-ink font-semibold border-t border-line mt-2 pt-2">
                                    <span>Delivery ৳{order.deliveryCharge ?? 0} · Grand Total</span>
                                    <span className="font-mono">৳{order.grandTotal}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <button
                              onClick={() => handleDelete(order._id)}
                              className="mt-4 text-xs font-semibold text-red-600 hover:text-red-700"
                            >
                              Delete Order
                            </button>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination && pagination.totalCount > 0 && (
        <div className="flex items-center gap-3 mt-4">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 border border-line rounded disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-sub">
            Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} orders)
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 border border-line rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Order;
